import { useMemo } from "react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/components/chart";
import type { TimeseriesPoint } from "@/lib/api";

const PALETTE = ["var(--chart-2)", "var(--chart-1)", "var(--positive)", "var(--warning)", "var(--chart-4)"];

const timeLabel = (v: number) =>
  new Date(v * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export function SeriesChart({
  data,
  format,
}: {
  data: TimeseriesPoint[];
  format: (v: number) => string;
}) {
  const { rows, config } = useMemo(() => {
    const groups = [...new Set(data.map((d) => d.grp ?? "other"))];
    const buckets = new Map<number, Record<string, number>>();
    for (const d of data) {
      // A group missing from a bucket is 0, not undefined: undefined breaks the stacked line.
      const row = buckets.get(d.bucket) ?? { ts: d.bucket, ...Object.fromEntries(groups.map((g) => [g, 0])) };
      row[d.grp ?? "other"] += d.value;
      buckets.set(d.bucket, row);
    }
    const config = Object.fromEntries(
      groups.map((g, i) => [g, { label: g, color: PALETTE[i % PALETTE.length] }]),
    ) satisfies ChartConfig;
    return { rows: [...buckets.values()].sort((a, b) => a.ts - b.ts), config };
  }, [data]);

  if (rows.length === 0)
    return <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">No data yet.</div>;

  return (
    <ChartContainer config={config} className="aspect-auto h-[260px] w-full">
      <AreaChart data={rows} margin={{ left: 0, right: 8, top: 8 }}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="ts"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={24}
          tickFormatter={timeLabel}
        />
        <YAxis tickLine={false} axisLine={false} width={52} tickFormatter={format} />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(_, payload) =>
                payload?.[0] ? timeLabel(Number(payload[0].payload.ts)) : ""
              }
              formatter={(value, name) => (
                <span className="flex w-full justify-between gap-4">
                  <span className="text-muted-foreground">{name}</span>
                  <span className="tabular-nums">{format(Number(value))}</span>
                </span>
              )}
            />
          }
        />
        {Object.keys(config).map((g) => (
          <Area
            key={g}
            isAnimationActive={false}
            type="monotone"
            dataKey={g}
            stackId="a"
            stroke={`var(--color-${g})`}
            fill={`var(--color-${g})`}
            fillOpacity={0.2}
            strokeWidth={1.5}
          />
        ))}
        <ChartLegend
          verticalAlign="top"
          content={<ChartLegendContent className="justify-start pb-6" />}
        />
      </AreaChart>
    </ChartContainer>
  );
}
